import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink, Navigate, useParams } from "react-router-dom";
import SeoHelmet from "../seo/SeoHelmet";
import {
  absoluteUrl,
  buildBreadcrumbSchema,
  buildFaqSchema,
} from "../seo/schema";
import { blogCategories } from "../data/blogCategories";
import { blogPosts } from "../data/blogPosts";
import { getPostsByCategory } from "./blogUtils";
import BlogCard from "./BlogCard";

function BlogCategory() {
  const { category: categoryParam } = useParams();

  const category = useMemo(
    () => blogCategories.find((item) => item.slug === categoryParam),
    [categoryParam]
  );

  const posts = useMemo(
    () => (category ? getPostsByCategory(category.slug, blogPosts) : []),
    [category]
  );

  const relatedCategories = useMemo(
    () =>
      blogCategories
        .filter((item) => item.slug !== categoryParam)
        .slice(0, 4),
    [categoryParam]
  );

  if (!category) {
    return <Navigate to="/blog" replace />;
  }

  const categoryCanonical = absoluteUrl(`/blog/category/${category.slug}`);

  const schema = [
    buildBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Blog", path: "/blog" },
      { name: category.name, path: `/blog/category/${category.slug}` },
    ]),
  ];

  if (category.isPillar && Array.isArray(category.faqs) && category.faqs.length) {
    schema.push(buildFaqSchema(category.faqs));
  }

  return (
    <Container maxWidth="lg">
      <SeoHelmet
        title={`${category.name} | Cyberpunk Blog`}
        description={
          category.intro ||
          "Investigation-focused CDR and telecom workflow resources for lawful digital evidence handling."
        }
        canonical={categoryCanonical}
        ogTitle={`${category.name} | Cyberpunk`}
        ogDescription={category.intro}
        ogUrl={categoryCanonical}
        schema={schema}
      />

      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
            {category.name}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5 }}>
            {category.intro}
          </Typography>
          {category.isPillar ? <Chip size="small" color="primary" label="Pillar Hub" /> : null}
        </Box>

        <Card variant="outlined">
          <CardContent>
            <Box
              sx={{
                "& h2": { mt: 2.5, mb: 1.2, fontSize: "1.35rem" },
                "& p": { mb: 1.5, color: "text.secondary" },
                "& ul": { pl: 2.2, mb: 1.5 },
                "& li": { mb: 0.8, color: "text.secondary" },
                "& a": { color: "primary.main" },
              }}
              dangerouslySetInnerHTML={{
                __html: category.isPillar
                  ? `${category.introHtml || ""}${category.hubContentHtml || ""}`
                  : category.introHtml || "",
              }}
            />
          </CardContent>
        </Card>

        {category.isPillar && posts.length ? (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h5" component="h2" sx={{ mb: 1.5 }}>
                Cluster Posts
              </Typography>
              <Stack spacing={1}>
                {blogPosts.map((post) => (
                  <Typography
                    key={post.id}
                    component={RouterLink}
                    to={`/blog/${post.slug}`}
                    sx={{
                      color: "text.primary",
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {post.title}
                  </Typography>
                ))}
              </Stack>
            </CardContent>
          </Card>
        ) : null}

        {category.isPillar && Array.isArray(category.faqs) && category.faqs.length ? (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h5" component="h2" sx={{ mb: 1.5 }}>
                FAQ
              </Typography>
              <Stack spacing={1.5}>
                {category.faqs.map((faq) => (
                  <Box key={faq.question}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {faq.question}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {faq.answer}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        ) : null}

        <Box>
          <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
            Posts In This Category
          </Typography>
          {posts.length ? (
            <Grid container spacing={2}>
              {posts.map((post) => (
                <Grid key={post.id} item xs={12} md={6}>
                  <BlogCard post={post} categoryName={category.name} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography color="text.secondary">No posts available in this category yet.</Typography>
          )}
        </Box>

        <Box>
          <Typography variant="h6" component="h3" sx={{ mb: 1.5 }}>
            Related Categories
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {relatedCategories.map((item) => (
              <Chip
                key={item.slug}
                component={RouterLink}
                to={`/blog/category/${item.slug}`}
                label={item.name}
                clickable
                sx={{ textDecoration: "none" }}
              />
            ))}
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
}

export default BlogCategory;
